import { settleSpin, type SpinSettlement } from '../../widgets/slot/settleSpin';
import type { SpinOutcome } from '../../widgets/slot/types';
import type { Logic } from '../Logic';
import type { PaidReelGems } from './reelGems';
import type { SlotLogicContext } from './SlotLogicContext';

/** How many free spins a single scatter trigger awards. cs-core's MMS
 * uses 8; some variants use 10–15. Tuneable per game/theme later. */
const FREE_SPINS_PER_TRIGGER = 8;

export interface RunSpinResult {
  /** True iff the bet was debited (or a free spin consumed) and the spin
   * actually ran. */
  ran: boolean;
  /** True iff the spin was free (consumed a free-spin counter instead of
   * debiting the bet). */
  wasFreeSpin?: boolean;
  outcome?: SpinOutcome;
  settlement?: SpinSettlement;
  /** Number of free spins granted by this spin's scatter trigger; 0 if
   * the spin didn't trigger a bonus. */
  freeSpinsGranted?: number;
  /** Server spins only: the gem cells the server paid (`reel_gems`), what
   *  it paid for them and the spin's `wallet.gems`, which already has them.
   *  Absent when the server paid none. The settlement's gems are only the
   *  cells it left to the app. */
  reelGems?: PaidReelGems;
}

/**
 * Orchestrates a full spin: debit the bet, run the machine animation, settle
 * the result, mutate the model. The scene awaits the returned outcome /
 * settlement to drive the post-spin visual sequence (highlight wins, decide
 * gamble offer or auto-collect).
 *
 * <p>Lifted-in-spirit from cs-core's {@code SpinLogic.ts} + {@code WinningsLogic.ts}.
 * The model emits balance-changed and (when applicable) not-enough-coins;
 * the scene's listeners react. The scene also emits gamble-offered or runs
 * collect once it knows whether there was a win — those are downstream
 * decisions, not Run-Spin's responsibility.
 */
export class RunSpinLogic implements Logic<void, RunSpinResult> {
  constructor(private readonly ctx: SlotLogicContext) {}

  async execute(): Promise<RunSpinResult> {
    const { model, machine, winLines, payoutTable, wildSymbolId, scatterSymbolId } = this.ctx;

    const bet = model.currentBet;

    // Free spin first — bonus mode skips the bet debit. Falls through to
    // the normal affordability check if no free spins are banked.
    let wasFreeSpin = false;
    if (model.hasFreeSpinAvailable()) {
      model.consumeFreeSpin();
      wasFreeSpin = true;
    } else if (!model.debit(bet, 'spin-bet')) {
      // Affordability gate. Model emits not-enough-coins on failure; scene
      // listens and renders the message. We early-out so the caller knows
      // the spin didn't actually start.
      return { ran: false };
    }

    model.markSpinStarted();
    const settle = (drawn: SpinOutcome) =>
      settleSpin({
        outcome: drawn,
        winLines,
        payoutTable,
        bet,
        wildSymbolId,
        scatterSymbolId: scatterSymbolId ?? null,
      });
    // The last reel lands with coins when the spin wins.
    const outcome = await machine.spin({ winMultiple: (drawn) => (bet > 0 ? settle(drawn).totalWinnings / bet : 0) });
    const settlement = settle(outcome);

    // Scatter triggers award free spins. cs-core's MMS treats successive
    // triggers as additive — banked free spins keep stacking — so we just
    // grant on every settlement that has a scatter result.
    let freeSpinsGranted = 0;
    if (settlement.scatter !== null) {
      freeSpinsGranted = FREE_SPINS_PER_TRIGGER;
      model.grantFreeSpins(freeSpinsGranted, 'scatter');
    }

    model.markSpinLanded();
    return { ran: true, wasFreeSpin, outcome, settlement, freeSpinsGranted };
  }
}
